import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { ArrowLeft, Brain, User, Video, Search as SearchIcon, Download, Trash2, Loader2, Shield, RotateCcw, FilmIcon } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import MyCricketPanel from "../components/MyCricketPanel";
// Iter XX.267a — mobile bottom nav on the Memory surface so the
// navigation contract Home/Pulse/Gully/Memory/More holds across every
// primary route, not just /chat.
import PrimarySurfaceNav from "../components/PrimarySurfaceNav";
// Iter XX.267c — page error boundary so a render exception on iOS
// WKWebView surfaces visible text instead of a black screen.
import PageErrorBoundary from "../components/PageErrorBoundary";
// Iter XX.270 — "Build Your Gully" narrative (Phase 1). Factual
// accumulation sentence rendered inside the Memory tab. Gated by
// `gully_narrative_enabled`. Silent when all counts are zero.
import GullyKnowsLine from "../components/GullyKnowsLine";
import { useFlag } from "../lib/featureFlags";

const STORAGE_KEY = "gully_web_session_id";
const MEMORY_LAST_SEEN_KEY = "gully_memory_last_seen";

export default function Me() {
  return (
    <PageErrorBoundary pageName="Memory">
      <MeInner />
    </PageErrorBoundary>
  );
}

function MeInner() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const initialTab = ["knows", "profile", "reviews", "search", "account"].includes(searchParams.get("tab"))
    ? searchParams.get("tab") : "knows";
  const [tab, setTab] = useState(initialTab);
  const [sessionId, setSessionId] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY); } catch { return null; }
  });
  const [linked, setLinked] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(false);
  const [accountStatus, setAccountStatus] = useState(null);

  // Iter XX.47 — /me must be directly accessible on fresh login,
  // refresh, and deep-links. If we don't have a session yet but the
  // user IS authenticated, silently bootstrap one via POST /web/session
  // (same endpoint Chat uses). This removes the "visit chat first"
  // dead-end that made Memory feel hidden.
  useEffect(() => {
    if (sessionId) return;
    if (!user) return;   // Wait for auth to resolve before creating.
    let alive = true;
    setBootstrapping(true);
    (async () => {
      try {
        let bid = null;
        try {
          // eslint-disable-next-line global-require
          bid = (require("../lib/browserId").getBrowserId)();
        } catch { /* noop */ }
        const { data } = await api.post("/web/session", { browser_id: bid });
        if (!alive) return;
        if (data?.session_id) {
          setSessionId(data.session_id);
          try { localStorage.setItem(STORAGE_KEY, data.session_id); } catch { /* ignore */ }
        }
      } catch {
        /* fall through — user still sees the "start a chat first" hint */
      } finally {
        if (alive) setBootstrapping(false);
      }
    })();
    return () => { alive = false; };
  }, [sessionId, user]);

  useEffect(() => {
    if (!sessionId) return;
    let bid = null;
    try {
      // eslint-disable-next-line global-require
      bid = (require("../lib/browserId").getBrowserId)();
    } catch { /* noop */ }
    api.get("/web/me", { params: { session_id: sessionId, browser_id: bid } })
       .then(r => setLinked(!!r.data?.is_linked))
       .catch(() => setLinked(false));
    api.get("/web/account-status", { params: { session_id: sessionId, browser_id: bid } })
       .then(r => setAccountStatus(r.data))
       .catch(() => setAccountStatus(null));
  }, [sessionId]);

  if (!user) {
    // Iter XX.49 — logged-out first-time UX. Never mention phone
    // numbers or "authentication". Value-first, then a single CTA.
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-neutral-100 flex items-center justify-center px-6" data-testid="me-logged-out">
        <div className="max-w-md w-full text-center">
          <div className="text-5xl mb-4">🧠</div>
          <h1 className="text-3xl font-display font-light mb-3" data-testid="me-logged-out-title">Your Cricket Memory</h1>
          <p className="text-neutral-400 leading-relaxed mb-8">
            Every match. Every lesson. Every breakthrough.<br/>
            Remembered automatically.<br/><br/>
            Sign in to start building your Cricket Memory.
          </p>
          <Link
            to="/login?return_to=/me"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-500 text-neutral-950 font-medium hover:bg-emerald-400 transition-colors"
            data-testid="me-logged-out-cta"
          >
            Continue with Google
          </Link>
        </div>
      </div>
    );
  }
  if (!sessionId) {
    if (bootstrapping) return <Centered>Loading your Cricket Memory…</Centered>;
    return <Centered>Loading your Cricket Memory…</Centered>;
  }
  // Iter XX.52 — Memory is Free. Google auth is sufficient identity.
  // Phone number is NO LONGER a blocker — it becomes an optional
  // Account preference (surfaced inside the Account tab only). Every
  // signed-in user goes straight into their Memory.

  async function restore() {
    try {
      await api.post("/web/restore-account", new URLSearchParams({ session_id: sessionId }),
                     { headers: { "Content-Type": "application/x-www-form-urlencoded" } });
      toast.success("Account restored.");
      const r = await api.get("/web/account-status", { params: { session_id: sessionId } });
      setAccountStatus(r.data);
    } catch { toast.error("Couldn't restore."); }
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-neutral-100" data-testid="me-screen">
      <div className="sticky top-0 z-10 border-b border-neutral-800 bg-[#0a0a0a]/95 backdrop-blur px-4 py-3 flex items-center justify-between"
           style={{ paddingTop: "calc(0.75rem + env(safe-area-inset-top, 0px))" }}>
        <Link to="/chat" className="flex items-center gap-2 text-sm text-neutral-400 hover:text-white" data-testid="me-back-chat">
          <ArrowLeft size={16} /> Chat
        </Link>
        <div className="text-sm flex items-center gap-1.5"><Brain size={14} className="text-emerald-400" /> Cricket Memory</div>
        <div className="flex items-center gap-3">
          <Link to="/earn" className="text-xs text-emerald-300 hover:text-white flex items-center gap-1" data-testid="me-earn-link">
            Earn
          </Link>
          <Link to="/trust" className="text-xs text-neutral-500 hover:text-neutral-300 flex items-center gap-1" data-testid="me-trust-link">
            <Shield size={12}/> Trust
          </Link>
        </div>
      </div>

      {/* Soft-delete restore banner */}
      {accountStatus?.is_pending_deletion && (
        <div className="bg-amber-950/50 border-b border-amber-900/50 px-4 py-3 text-sm text-amber-200 flex items-center justify-between"
             data-testid="me-restore-banner">
          <span>Account scheduled for deletion. Purges {accountStatus.purge_at?.slice(0, 10)}.</span>
          <button onClick={restore} className="text-amber-300 hover:text-amber-100 underline" data-testid="me-restore-btn">Restore</button>
        </div>
      )}

      <div className="px-3 pt-3 max-w-2xl mx-auto pb-mobile-nav" data-testid="me-content-wrapper">
        {/* Iter XX.52 — first-time introduction. Shown once, dismissed
            forever via localStorage. The purpose of Memory is to make
            the player feel Gully actually remembers them — this
            intro protects that feeling. Never a paywall, never a CTA. */}
        <MemoryFirstTimeIntro />

        {/* Iter XX.50 — positioning tagline. Answers the one question
            this screen exists to answer: "what has Gully learned about me?" */}
        <div className="text-xs text-emerald-400/80 uppercase tracking-widest mb-2 mt-1"
             data-testid="me-positioning-eyebrow">
          Your Personal Cricket Intelligence System
        </div>
        <p className="text-sm text-neutral-400 mb-4 leading-relaxed"
           data-testid="me-positioning-sub">
          What Gully has learned about your cricket — patterns, strengths,
          confidence and the moments that shape your season.
        </p>
        <div className="flex gap-1 overflow-x-auto border-b border-neutral-800 mb-4" data-testid="me-tabs">
          {[
            { id: "knows", label: "Memory", icon: Brain },
            { id: "profile", label: "Profile", icon: User },
            { id: "reviews", label: "Reviews", icon: Video },
            { id: "search", label: "Search", icon: SearchIcon },
            { id: "account", label: "Account", icon: Shield },
          ].map(t => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`px-3 py-2.5 text-sm flex items-center gap-1.5 whitespace-nowrap border-b-2 ${
                  tab === t.id ? "border-emerald-500 text-white" : "border-transparent text-neutral-400 hover:text-neutral-200"
                }`}
                data-testid={`me-tab-${t.id}`}>
                <Icon size={14}/> {t.label}
              </button>
            );
          })}
        </div>

        {tab === "knows" && <KnowsTab sessionId={sessionId} />}
        {tab === "profile" && <ProfileTab sessionId={sessionId} />}
        {tab === "reviews" && <ReviewsTab sessionId={sessionId} />}
        {tab === "search" && <SearchTab sessionId={sessionId} />}
        {tab === "account" && <AccountTab sessionId={sessionId} accountStatus={accountStatus} setAccountStatus={setAccountStatus} />}

        {/* Iter XX.52 — calm bottom section. NEVER a paywall or grid.
            Only rendered on the Memory tab. Never on emotionally
            fragile screens. */}
        {tab === "knows" && <MemoryPlusSoftInvite />}
      </div>
      {/* Iter XX.267a — bottom nav on /me. Memory tab remains
          highlighted; other tabs navigate back to /chat with a hash
          hint that Chat.jsx interprets. */}
      <div className="sm:hidden">
        <PrimarySurfaceNav currentRoute="memory" />
      </div>
    </div>
  );
}

const MEMORY_INTRO_KEY = "gully_memory_intro_seen_v1";

function MemoryFirstTimeIntro() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    try {
      if (!localStorage.getItem(MEMORY_INTRO_KEY)) setVisible(true);
    } catch { /* ignore */ }
  }, []);
  if (!visible) return null;
  const dismiss = () => {
    setVisible(false);
    try { localStorage.setItem(MEMORY_INTRO_KEY, String(Date.now())); } catch { /* ignore */ }
  };
  return (
    <div
      className="rounded-2xl border border-emerald-800/40 bg-gradient-to-br from-emerald-950/40 to-neutral-950 p-5 mb-4"
      data-testid="me-first-time-intro"
    >
      <div className="flex items-start gap-3">
        <div className="text-2xl">🧠</div>
        <div className="flex-1">
          <div className="text-neutral-100 text-base font-medium mb-1">
            This is your Cricket Memory.
          </div>
          <p className="text-sm text-neutral-300 leading-relaxed">
            Everything you share with Gully helps build a picture of
            your game over time.
          </p>
          <button
            onClick={dismiss}
            className="mt-3 text-xs text-emerald-300 hover:text-emerald-200"
            data-testid="me-first-time-intro-dismiss"
          >
            Got it →
          </button>
        </div>
      </div>
    </div>
  );
}

function MemoryPlusSoftInvite() {
  return (
    <div className="mt-8 mb-8 px-4 py-6 rounded-xl border border-neutral-900 bg-neutral-950/60"
         data-testid="me-plus-soft-invite">
      <div className="text-sm text-neutral-300 mb-2">
        Want to understand your cricket even better?
      </div>
      <p className="text-sm text-neutral-500 leading-relaxed">
        Gully Plus goes further — season-wide patterns, confidence
        trends, deeper long-term insights and advanced pattern
        analysis. A natural next step whenever you&apos;re ready.
      </p>
    </div>
  );
}

function Centered({ children }) {
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-neutral-300 flex items-center justify-center p-6 text-center">
      <div>{children}</div>
    </div>
  );
}

function KnowsTab({ sessionId }) {
  const [data, setData] = useState(null);
  // Iter XX.270 — narrative visibility flag. Guards the factual
  // accumulation sentence. Component collapses to silence when
  // all counts are zero even if the flag is ON.
  const gullyNarrativeOn = useFlag("gully_narrative_enabled");
  useEffect(() => {
    let bid = null;
    try {
      // eslint-disable-next-line global-require
      bid = (require("../lib/browserId").getBrowserId)();
    } catch { /* noop */ }
    api.get("/web/knows", { params: { session_id: sessionId, browser_id: bid } })
       .then(r => setData(r.data))
       .catch(() => setData({ is_linked: false, patterns: [], strengths: [], stats: null }));
  }, [sessionId]);

  // Iter XX.47 — memory "last seen" bookkeeping. When the tab mounts
  // with fresh data, note the current insight-count fingerprint so we
  // clear any "new insight" badge on the primary nav.
  useEffect(() => {
    if (!data) return;
    try {
      const fp = memoryFingerprint(data);
      localStorage.setItem(MEMORY_LAST_SEEN_KEY, String(fp));
      // Notify any listener (e.g. chat header badge).
      window.dispatchEvent(new CustomEvent("gully:memory-seen", { detail: { fp } }));
    } catch { /* ignore */ }
  }, [data]);

  if (!data) return <Loader/>;

  // Iter XX.52 — Memory is Free. Phone linkage is no longer a blocker.
  // Compute a simple "how much have we actually captured?" count so we
  // can show an emotionally warm "your Memory has started" state
  // instead of a blank screen when the data is thin.
  const dedupedPatterns = dedupePatterns(data.patterns);
  const populatedItems =
    (data.strengths?.length || 0) +
    (dedupedPatterns?.length || 0) +
    (data.pressure_triggers?.length || 0) +
    (data.recurring_mistakes?.length || 0) +
    (data.recent_improvements?.length || 0) +
    (data.still_needs_work?.length || 0) +
    (data.most_discussed_topics?.length || 0);

  if (populatedItems === 0) {
    return (
      <div className="pb-4" data-testid="knows-tab-empty">
        <div className="rounded-2xl border border-neutral-800 bg-neutral-950 p-6"
             data-testid="knows-empty-hero">
          <div className="text-4xl mb-3">🌱</div>
          <div className="text-neutral-100 text-lg font-medium mb-2">
            Your Cricket Memory has started.
          </div>
          <p className="text-sm text-neutral-400 leading-relaxed mb-5">
            Every match, practice and conversation helps Gully understand
            your cricket better. You&apos;re just getting started.
          </p>
          <Link
            to="/chat"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500 text-neutral-950 text-sm font-medium hover:bg-emerald-400 transition-colors"
            data-testid="knows-empty-cta"
          >
            Start chatting →
          </Link>
        </div>
        {/* Show whatever exists, even if it's just stats. */}
        {data.stats && <div className="mt-4"><Stats stats={data.stats} /></div>}
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-4" data-testid="knows-tab">
      {gullyNarrativeOn && <GullyKnowsLine data={data} />}
      <Stats stats={data.stats} />
      <MyCricketPanel />
      <Section title="Strengths" items={data.strengths} testid="knows-strengths" tone="emerald" />
      <Section title="Recurring patterns" items={dedupedPatterns} testid="knows-patterns" />
      <Section title="Pressure triggers" items={data.pressure_triggers} testid="knows-triggers" tone="amber" />
      <Section title="Recurring mistakes (from video reviews)" items={data.recurring_mistakes} testid="knows-mistakes" tone="amber" />
      <Section title="Recent improvements" items={data.recent_improvements} testid="knows-improvements" tone="emerald" />
      <Section title="Still needs work" items={data.still_needs_work} testid="knows-needs-work" />
      <TopicCloud topics={data.most_discussed_topics || []} />
      <CueHistory cues={data.match_day_cues_recent || []} />
    </div>
  );
}

// Iter XX.47 — Fingerprint of the current memory payload. Any change
// in the count of recurring insights bumps the fingerprint, which
// drives the "new insight" badge on the primary nav.
function memoryFingerprint(data) {
  if (!data) return "0";
  const c = (data.strengths?.length || 0) * 1
          + (data.patterns?.length || 0) * 3
          + (data.recurring_mistakes?.length || 0) * 5
          + (data.recent_improvements?.length || 0) * 7
          + (data.still_needs_work?.length || 0) * 11
          + (data.stats?.video_reviews || 0) * 13;
  return String(c);
}

// Collapse patterns whose text is near-identical. We normalise text
// (lowercase + strip punctuation + collapse whitespace) then group by
// the first six words. If a group has ≥ 2 patterns, we keep the
// longest text and append a "Seen in N similar reflections" line.
function dedupePatterns(patterns) {
  if (!Array.isArray(patterns) || patterns.length === 0) return [];
  const groups = new Map();
  for (const p of patterns) {
    const raw = typeof p === "string" ? p : (p?.text || p?.body || p?.title || "");
    if (!raw) continue;
    const norm = raw.toLowerCase().replace(/[^a-z0-9\s]/g, " ").replace(/\s+/g, " ").trim();
    const key = norm.split(" ").slice(0, 6).join(" ");
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push({ raw, norm, orig: p });
  }
  const out = [];
  for (const arr of groups.values()) {
    // Pick the longest raw text as canonical.
    arr.sort((a, b) => b.raw.length - a.raw.length);
    if (arr.length === 1) {
      out.push(arr[0].orig);
    } else {
      const canonical = arr[0].raw;
      out.push({
        text: canonical,
        note: `Seen in ${arr.length} similar moments`,
      });
    }
  }
  return out;
}

function Stats({ stats }) {
  if (!stats) return null;
  return (
    <div className="grid grid-cols-2 gap-2" data-testid="knows-stats">
      <Stat label="Video reviews" value={stats.video_reviews} />
      <Stat label="Conversation turns" value={stats.conversation_turns} />
    </div>
  );
}
function Stat({ label, value }) {
  return (
    <div className="rounded-2xl border border-neutral-800 bg-neutral-950 p-4">
      <div className="text-2xl font-semibold">{value ?? 0}</div>
      <div className="text-xs text-neutral-400 mt-1">{label}</div>
    </div>
  );
}

function Section({ title, items, testid, tone }) {
  if (!items || items.length === 0) return null;
  const dot = tone === "emerald" ? "bg-emerald-500" : tone === "amber" ? "bg-amber-500" : "bg-neutral-500";
  return (
    <div className="rounded-2xl border border-neutral-800 bg-neutral-950 p-4" data-testid={testid}>
      <div className="text-sm text-neutral-400 mb-2">{title}</div>
      <ul className="space-y-1.5">
        {items.map((it, i) => {
          const text = typeof it === "string" ? it : (it.pattern || it.text || JSON.stringify(it));
          const note = typeof it === "object" && it && it.note ? it.note : null;
          return (
            <li key={i} className="flex gap-2 text-sm leading-snug" data-testid={`${testid}-item-${i}`}>
              <span className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-none ${dot}`} />
              <span>
                {text}
                {note && (
                  <span className="block text-xs text-emerald-400/70 mt-0.5" data-testid={`${testid}-note-${i}`}>
                    · {note}
                  </span>
                )}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function TopicCloud({ topics }) {
  if (!topics.length) return null;
  return (
    <div className="rounded-2xl border border-neutral-800 bg-neutral-950 p-4" data-testid="knows-topics">
      <div className="text-sm text-neutral-400 mb-3">Most-discussed topics</div>
      <div className="flex flex-wrap gap-2">
        {topics.map((t, i) => (
          <span key={i} className="px-2.5 py-1 rounded-full bg-neutral-900 border border-neutral-800 text-xs">
            {t.topic} <span className="text-neutral-500">·{t.count}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function CueHistory({ cues }) {
  if (!cues.length) return null;
  return (
    <div className="rounded-2xl border border-neutral-800 bg-neutral-950 p-4" data-testid="knows-cues">
      <div className="text-sm text-neutral-400 mb-3">Recent match-day cues</div>
      <ul className="space-y-1.5">
        {cues.map((c, i) => (
          <li key={i} className="text-sm">→ <em>{c.cue}</em></li>
        ))}
      </ul>
    </div>
  );
}

function ProfileTab({ sessionId }) {
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/web/profile", { params: { session_id: sessionId } })
       .then(r => { setProfile(r.data?.profile); setForm(r.data?.profile || {}); });
  }, [sessionId]);

  if (!profile) return <Loader/>;

  async function save() {
    setSaving(true);
    try {
      await api.patch("/web/profile", { session_id: sessionId, ...form });
      toast.success("Saved");
      const r = await api.get("/web/profile", { params: { session_id: sessionId } });
      setProfile(r.data?.profile); setForm(r.data?.profile || {});
    } catch (e) { toast.error("Couldn't save."); }
    finally { setSaving(false); }
  }

  const update = (k, v) => setForm({ ...form, [k]: v });

  return (
    <div className="space-y-3 pb-4" data-testid="profile-tab">
      <Field k="name"          label="Name"             form={form} update={update} />
      <Field k="role"          label="Primary role"     form={form} update={update}
             opts={["batter", "fast_bowler", "spinner", "wicketkeeper", "all_rounder"]} />
      <Field k="age_group"     label="Age group"        form={form} update={update}
             opts={["u14", "u16", "u19", "club", "provincial", "senior"]} />
      <Field k="country"       label="Country"          form={form} update={update} />
      <Field k="team"          label="Team"             form={form} update={update} />
      <Field k="bowling_style" label="Bowling style"    form={form} update={update} />
      <Field k="batting_style" label="Batting style"    form={form} update={update} />
      <Field k="instagram"     label="Instagram handle" form={form} update={update} />
      <Field k="goals"         label="Primary goal"     form={form} update={update} />
      <button onClick={save} disabled={saving}
              className="w-full py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 font-medium"
              data-testid="profile-save-btn">
        {saving ? "Saving…" : "Save profile"}
      </button>
    </div>
  );
}

function Field({ k, label, form, update, opts }) {
  return (
    <label className="block text-sm">
      <div className="text-neutral-400 mb-1">{label}</div>
      {opts ? (
        <select value={form[k] || ""} onChange={e => update(k, e.target.value)}
                className="w-full bg-neutral-900 border border-neutral-800 rounded-xl px-3 py-2.5 text-sm" data-testid={`profile-${k}`}>
          <option value="">—</option>
          {opts.map(o => <option key={o} value={o}>{o.replace(/_/g, " ")}</option>)}
        </select>
      ) : (
        <input value={form[k] || ""} onChange={e => update(k, e.target.value)}
               className="w-full bg-neutral-900 border border-neutral-800 rounded-xl px-3 py-2.5 text-sm" data-testid={`profile-${k}`} />
      )}
    </label>
  );
}

function ReviewsTab({ sessionId }) {
  const [list, setList] = useState(null);
  useEffect(() => {
    api.get("/web/reviews", { params: { session_id: sessionId, limit: 30 } })
       .then(r => setList(r.data?.reviews || []));
  }, [sessionId]);
  if (!list) return <Loader/>;
  if (list.length === 0) return <div className="text-neutral-400 text-sm py-8 text-center">No video reviews yet. Upload a clip from chat.</div>;
  return (
    <div className="space-y-2 pb-4" data-testid="reviews-tab">
      {list.map(r => (
        <div key={r.id} className="rounded-xl border border-neutral-800 bg-neutral-950 p-3 flex justify-between items-start gap-3">
          <div>
            <div className="text-sm font-medium capitalize">{r.kind || "Review"}</div>
            {r.match_day_cue && <div className="text-sm text-neutral-300 mt-1">→ <em>{r.match_day_cue}</em></div>}
          </div>
          <div className="text-xs text-neutral-500 whitespace-nowrap">{(r.created_at || "").slice(0, 10)}</div>
        </div>
      ))}
    </div>
  );
}

function SearchTab({ sessionId }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);

  async function go() {
    if (q.trim().length < 2) return;
    setBusy(true);
    try {
      const r = await api.get("/web/search", { params: { session_id: sessionId, q } });
      setResults(r.data);
    } catch (e) { toast.error("Search failed."); }
    finally { setBusy(false); }
  }

  return (
    <div className="space-y-3 pb-4" data-testid="search-tab">
      <div className="flex gap-2">
        <input value={q} onChange={e => setQ(e.target.value)}
               onKeyDown={e => e.key === "Enter" && go()}
               placeholder="Search your history… (release, googly, confidence)"
               className="flex-1 bg-neutral-900 border border-neutral-800 rounded-xl px-3 py-2.5 text-sm" data-testid="search-input" />
        <button onClick={go} disabled={busy}
                className="px-4 rounded-xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50" data-testid="search-go-btn">
          {busy ? <Loader2 size={16} className="animate-spin"/> : <SearchIcon size={16}/>}
        </button>
      </div>
      {results && (
        <>
          {results.messages?.length > 0 && (
            <div className="rounded-2xl border border-neutral-800 bg-neutral-950 p-4">
              <div className="text-sm text-neutral-400 mb-2">Conversations</div>
              {results.messages.slice(0, 20).map((m, i) => (
                <div key={i} className="text-sm border-t border-neutral-900 first:border-0 py-2">
                  <span className="text-xs text-neutral-500">{m.role}</span>
                  <div className="text-neutral-200">{m.content}</div>
                </div>
              ))}
            </div>
          )}
          {results.reviews?.length > 0 && (
            <div className="rounded-2xl border border-neutral-800 bg-neutral-950 p-4">
              <div className="text-sm text-neutral-400 mb-2">Reviews</div>
              {results.reviews.map((r, i) => (
                <div key={i} className="text-sm border-t border-neutral-900 first:border-0 py-2">
                  <span className="capitalize">{r.kind}</span> · <em>{r.cue}</em>
                </div>
              ))}
            </div>
          )}
          {!results.messages?.length && !results.reviews?.length &&
            <div className="text-neutral-500 text-sm text-center py-6">Nothing matched.</div>}
        </>
      )}
    </div>
  );
}

function Loader() {
  return <div className="flex items-center justify-center py-10 text-neutral-500"><Loader2 className="animate-spin"/></div>;
}

async function exportData(sessionId) {
  try {
    const { data } = await api.get("/web/export", { params: { session_id: sessionId } });
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `gully-export-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Exported.");
  } catch (e) { toast.error("Export failed."); }
}

function AccountPhoneRow({ sessionId }) {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    api.get("/web/me", { params: { session_id: sessionId } })
       .then(r => {
         const p = r.data?.session?.phone_e164 || r.data?.session?.phone || null;
         setStatus({ phone: p, linked: !!p });
       })
       .catch(() => setStatus({ phone: null, linked: false }));
  }, [sessionId]);
  if (!status) return null;
  const body = status.linked
    ? `Phone connected · Cricket Memory syncs across your devices.`
    : `Optional — connect a phone to keep your Memory across devices.`;
  return (
    <ActionRow
      title="Phone number"
      body={body}
      cta={status.linked ? (
        <span className="text-xs text-emerald-400 px-3 py-1 rounded-full border border-emerald-800/40">Connected</span>
      ) : (
        <Link
          to="/chat?add_phone=1"
          className="px-4 py-2 rounded-xl border border-neutral-800 hover:border-emerald-700 hover:bg-emerald-950/30 text-sm inline-flex items-center gap-1.5"
          data-testid="account-add-phone-btn"
        >
          Add phone
        </Link>
      )}
    />
  );
}

function AccountTab({ sessionId, accountStatus, setAccountStatus }) {
  const [partner, setPartner] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/partners/me")
      .then(r => { if (alive) setPartner(r.data?.partner || null); })
      .catch(() => { if (alive) setPartner(null); });
    return () => { alive = false; };
  }, []);

  async function deleteVideos() {
    if (!window.confirm("Delete all your video reviews? Conversations stay. This is permanent.")) return;
    try {
      const fd = new URLSearchParams({ session_id: sessionId });
      const { data } = await api.post("/web/delete-videos", fd,
        { headers: { "Content-Type": "application/x-www-form-urlencoded" } });
      toast.success(`Deleted ${data?.deleted || 0} videos.`);
    } catch { toast.error("Couldn't delete videos."); }
  }

  async function softDelete() {
    if (!window.confirm("Schedule your account for deletion? You can restore within 30 days.")) return;
    try {
      const fd = new URLSearchParams({ session_id: sessionId });
      const { data } = await api.post("/web/soft-delete-account", fd,
        { headers: { "Content-Type": "application/x-www-form-urlencoded" } });
      setAccountStatus((s) => ({ ...(s || {}), is_pending_deletion: true,
                                 purge_at: data?.purge_at, window_days: data?.window_days }));
      toast.message("Account scheduled for deletion. You have 30 days to restore.");
    } catch { toast.error("Couldn't schedule deletion."); }
  }

  async function hardDelete() {
    if (!window.confirm("Permanently delete EVERYTHING — chats, videos, memory? This cannot be undone.")) return;
    const second = window.prompt("Type DELETE to confirm.");
    if (second !== "DELETE") { toast.message("Cancelled."); return; }
    try {
      const fd = new URLSearchParams({ session_id: sessionId, confirm: "DELETE" });
      await api.post("/web/delete-account", fd,
        { headers: { "Content-Type": "application/x-www-form-urlencoded" } });
      try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
      toast.success("Account deleted.");
      window.location.href = "/";
    } catch { toast.error("Delete failed."); }
  }

  return (
    <div className="space-y-3 pb-8" data-testid="account-tab">
      {/* Iter XX.52 — phone linkage becomes a calm Account preference,
          NEVER a Memory blocker. Only surfaced here; never interrupts
          the Memory experience. */}
      <AccountPhoneRow sessionId={sessionId} />

      {partner ? (
        <ActionRow
          title="Your Earn Hub"
          body={<>Your page is live at <span className="text-emerald-300">askgully.com/@{partner.username}</span> · {partner.status}.</>}
          cta={<a
                href="/earn"
                className="px-4 py-2 rounded-xl border border-emerald-700 hover:border-emerald-500 hover:bg-emerald-950/30 text-sm inline-flex items-center gap-1.5 text-emerald-300"
                data-testid="account-open-partner-dashboard">
                Open Earn →
               </a>}
        />
      ) : (
        <ActionRow
          title="Earn with Gully"
          body="Invite the cricketers in your world. Get a permanent page, a referral link, and a wallet — all free to claim."
          cta={<a
                href="/earn"
                className="px-4 py-2 rounded-xl border border-emerald-700 hover:border-emerald-500 hover:bg-emerald-950/30 text-sm inline-flex items-center gap-1.5 text-emerald-300"
                data-testid="account-become-partner-btn">
                Explore Earn →
               </a>}
        />
      )}
      <ActionRow
        title="Export your data"
        body="Download everything Gully knows about you as JSON — conversations, reviews, memory."
        cta={<button onClick={() => exportData(sessionId)}
                     className="px-4 py-2 rounded-xl border border-neutral-800 hover:border-emerald-700 hover:bg-emerald-950/30 text-sm inline-flex items-center gap-1.5"
                     data-testid="account-export-btn">
              <Download size={14}/> Export JSON
             </button>}
      />
      <ActionRow
        title="Delete just my videos"
        body="Removes all video reviews. Your conversations and patterns stay intact."
        cta={<button onClick={deleteVideos}
                     className="px-4 py-2 rounded-xl border border-neutral-800 hover:border-amber-700 hover:bg-amber-950/30 text-sm inline-flex items-center gap-1.5 text-amber-200"
                     data-testid="account-delete-videos-btn">
              <FilmIcon size={14}/> Delete videos
             </button>}
      />
      <ActionRow
        title="Delete my account (reversible 30 days)"
        body="Schedules your account for deletion. You can restore it any time in the next 30 days."
        cta={accountStatus?.is_pending_deletion ? (
          <button disabled className="px-4 py-2 rounded-xl border border-neutral-800 opacity-50 text-sm inline-flex items-center gap-1.5">
            <Trash2 size={14}/> Scheduled
          </button>
        ) : (
          <button onClick={softDelete}
                  className="px-4 py-2 rounded-xl border border-neutral-800 hover:border-red-700 hover:bg-red-950/30 text-sm inline-flex items-center gap-1.5 text-red-300"
                  data-testid="account-soft-delete-btn">
            <Trash2 size={14}/> Schedule deletion
          </button>
        )}
      />
      {accountStatus?.is_pending_deletion && (
        <ActionRow
          title="Restore account"
          body={`Your account will be permanently purged on ${accountStatus.purge_at?.slice(0, 10)}.`}
          cta={<button onClick={async () => {
            try {
              const fd = new URLSearchParams({ session_id: sessionId });
              await api.post("/web/restore-account", fd,
                { headers: { "Content-Type": "application/x-www-form-urlencoded" } });
              const r = await api.get("/web/account-status", { params: { session_id: sessionId } });
              setAccountStatus(r.data);
              toast.success("Account restored.");
            } catch { toast.error("Couldn't restore."); }
          }}
                  className="px-4 py-2 rounded-xl border border-emerald-900/50 hover:bg-emerald-950/30 text-sm inline-flex items-center gap-1.5 text-emerald-300"
                  data-testid="account-restore-btn">
            <RotateCcw size={14}/> Restore
          </button>}
        />
      )}
      <ActionRow
        title="Permanently delete now"
        body="Wipes your account and everything in it immediately. There's no coming back from this."
        cta={<button onClick={hardDelete}
                     className="px-4 py-2 rounded-xl border border-red-900 hover:bg-red-950/40 text-sm inline-flex items-center gap-1.5 text-red-400"
                     data-testid="account-hard-delete-btn">
              <Trash2 size={14}/> Delete forever
             </button>}
      />
      <div className="text-xs text-neutral-500 pt-2 leading-relaxed">
        Need more context? Read <Link to="/trust" className="text-emerald-400 hover:underline">Trust & Privacy</Link> —
        what Gully stores, how reviews work, and your rights.
      </div>
    </div>
  );
}

function ActionRow({ title, body, cta }) {
  return (
    <div className="rounded-2xl border border-neutral-800 bg-neutral-950 p-4 flex items-start justify-between gap-3">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium mb-1">{title}</div>
        <div className="text-xs text-neutral-400 leading-relaxed">{body}</div>
      </div>
      <div className="flex-none">{cta}</div>
    </div>
  );
}
